import { DateField } from "@fawkes/ui";
import { Fragment, useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { createPortal } from "react-dom";
import { useCustom, useCustomMutation, useDataProvider, useGetLocale, useTranslate } from "@refinedev/core";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Home,
  ImagePlus,
  LogIn,
  Mail,
  MapPin,
  Maximize2,
  Minimize2,
  PawPrint,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  ScanLine,
  Search,
  Sparkles,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";

import type { PhotoAiSuggestion } from "../../api/animalPhoto";
import { analyseerDierFoto, analyseerPaspoort, bewaarDierFoto } from "../../api/pensionDier";
import { useAnimalPhotoUrls } from "../../api/useAnimalPhotoUrls";
import { lookupPostcode } from "../../api/postcodeLookup";
import { todayIso } from "../../i18n/format";
import type { PensionDier, PensionReservering } from "../pensionKlantTypes";
import { dagenTussen, entingStaat, geslachtTeken, leeftijd, type Context } from "../pensionklant/hulp";
import {
  KlantBalk,
  Schakel,
  Terug,
  Veld,
  Voet,
  Volgende,
  datum,
  geldigePeriode,
  initialen,
  isDatum,
  langeDatum,
  useKlantDieren,
  type Beschikbaarheid,
  type GekozenKlant,
  type Periode,
  type Vertaal,
} from "./bouwstenen";
import { ArrangementenStap, arrangementenUrl, heeftArrangementen, type Extras } from "./ArrangementenStap";
import type { Arrangement } from "../pensionarrangement/arrangement";
import { VerblijfStap, type DierVerblijf } from "./VerblijfStap";
import type { Keuzes } from "./aanvraag";
import { ControleStap, KeuzesStap, Klaar } from "./KeuzesEnControle";
import { AdresZoeker } from "./AdresZoeker";
import { open as pensionOpen, tijdenVoor, useBrengHaaltijden, vensterTekst, venstersVoor } from "./tijden";
import { EigenElementen, EigenStap, GEEN_ANTWOORDEN, NIETS, TekstBlok, VerplichtMelding, useVerplicht } from "./EigenElementen";
import {
  blokAan,
  blokElement,
  blokTitel,
  eigenElementen,
  rondBlok,
  stapKop,
  stapNaam,
  standaardIndeling,
  useActieveIndeling,
  type ActieveIndeling,
  type Antwoorden,
  type StapSoort,
  type WizardElement,
  type WizardStap,
} from "./indeling";
import { LocatieContext, metLocatie, useLocatie, type Locatie } from "./locatie";
import "./NieuweReserveringWizard.css";

export type { GekozenKlant } from "./bouwstenen";
export { initialen } from "./bouwstenen";

/** Eén gevonden klant, uit GET /api/pension/klanten/zoeken. */
export interface GevondenKlant {
  id: number;
  klantNummer: number | null;
  naam: string;
  straat: string | null;
  huisnummer: string | null;
  toevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  email: string | null;
  telefoon: string | null;
  actief: boolean;
  dieren: number;
  pensionBoekingen: number;
}

const ZOEK_STANDAARD = standaardIndeling().stappen[0];
/** Zoveel klanten geeft de api hooguit terug. */
const MAX_GEVONDEN = 20;
const FOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const FOTO_MAX = 10 * 1024 * 1024;
const BIJZONDERHEDEN_MAX = 500;

// ── Hulpjes ──────────────────────────────────────────────────────────────────────────────────────────

function postcodeNetjes(postcode: string): string {
  const compact = postcode.replace(/\s/g, "").toUpperCase();
  return /^\d{4}[A-Z]{2}$/.test(compact) ? `${compact.slice(0, 4)} ${compact.slice(4)}` : postcode;
}

/** "Hogenhof 7, 7581 WL Losser". */
export function adresRegel(k: {
  straat: string | null;
  huisnummer: string | null;
  toevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
}): string | null {
  const straat = [k.straat, [k.huisnummer, k.toevoeging].filter(Boolean).join("")].filter(Boolean).join(" ");
  const plaats = [k.postcode ? postcodeNetjes(k.postcode) : null, k.woonplaats].filter(Boolean).join(" ");
  return [straat, plaats].filter(Boolean).join(", ") || null;
}

/** Uit een getypt adres de postcode en het huisnummer, om het formulier voor een nieuwe klant mee te vullen. */
export function uitAdres(adres: string): { postcode: string; huisnummer: string } {
  const postcode = /\b(\d{4})\s?([a-z]{2})\b/i.exec(adres);
  const zonderPostcode = postcode ? adres.replace(postcode[0], " ") : adres;
  const huisnummer = /\b(\d{1,5})\b/.exec(zonderPostcode);
  return {
    postcode: postcode ? `${postcode[1]} ${postcode[2].toUpperCase()}` : "",
    huisnummer: huisnummer ? huisnummer[1] : "",
  };
}

// ── De wizard ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Een nieuwe pensionreservering, in de stappen die de vestiging onder Instellingen › Pension heeft ingedeeld: klant
 * zoeken en kiezen (of aanmaken), dieren (of een nieuw dier), periode, verblijf &amp; verzorging, arrangementen,
 * reserveringskeuzes, eigen stappen, en controleren en aanmaken. Vanaf het klantscherm is de klant al bekend en
 * begint de wizard bij de dieren.
 *
 * <p>Levert het zoeken niets op, dan gaat de wizard meteen naar het formulier voor een nieuwe klant. Het venster
 * sluit niet bij een klik ernaast: dan ben je wat je invulde kwijt. Als voorbeeld vanuit de instellingen is alles te
 * bekijken, maar maakt de wizard niets aan.
 */
export function NieuweReserveringWizard({
  klant: vooraf = null,
  onSluit,
  indeling: vast,
  voorbeeld = false,
  nieuwDier: nieuwDierExtern,
}: {
  klant?: GekozenKlant | null;
  onSluit: () => void;
  /**
   * Alleen het klantportaal: "Nieuw dier toevoegen" opent daar de eigen wizard "Dier aanmelden" in plaats van het
   * formulier hieronder, en geeft het id van het nieuwe dier terug (of null bij annuleren).
   */
  nieuwDier?: () => Promise<number | null>;
  /** Deze indeling in plaats van de gepubliceerde: het voorbeeld in Instellingen › Pension. */
  indeling?: ActieveIndeling;
  /** Alleen bekijken: de wizard maakt niets aan en slaat niets op. */
  voorbeeld?: boolean;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const ctx: Context = useMemo(() => ({ t: (k, o) => t(k, o), locale, vandaag: todayIso() }), [t, locale]);
  const dataProvider = useDataProvider();

  // De locatie waarop deze aanvraag loopt. Een pension met meerdere vestigingen kiest hem in de stap Periode; de
  // server zegt welke locaties mogen en welke voorstaat (voor de medewerker zijn eigen vestiging, voor de klant die
  // van de vorige keer). Zolang dat antwoord er niet is blijft de keuze leeg en bepaalt de server de vestiging,
  // precies zoals voorheen.
  const [locatie, setLocatie] = useState<number | null>(null);
  const locatieData = useCustom<{ gekozen: number; keuzes: Locatie[] }>({
    url: "pension/wizard-vestigingen", method: "get", queryOptions: { enabled: !voorbeeld },
  }).data?.data;
  useEffect(() => {
    if (locatie == null && locatieData?.gekozen) setLocatie(locatieData.gekozen);
  }, [locatie, locatieData?.gekozen]);
  const locatieKeuze = useMemo(() => ({
    gekozen: locatie,
    keuzes: locatieData?.keuzes ?? [],
    kies: (id: number) => setLocatie(id),
  }), [locatie, locatieData?.keuzes]);

  const actief = useActieveIndeling(locatie);
  const { versie, indeling } = vast ?? actief;

  const [stapId, setStapId] = useState<string | null>(null);
  const [groot, setGroot] = useState(false);
  const [email, setEmail] = useState("");
  const [adres, setAdres] = useState("");
  const [gezocht, setGezocht] = useState("");
  const [resultaten, setResultaten] = useState<GevondenKlant[]>([]);
  const [keuze, setKeuze] = useState<GevondenKlant | null>(null);
  /** Stap Klant kiezen: de gevonden klanten, een klant controleren, of een nieuwe aanmaken. */
  const [klantScherm, setKlantScherm] = useState<"lijst" | "controleren" | "nieuw">("lijst");
  const [klant, setKlant] = useState<GekozenKlant | null>(vooraf);
  const [dieren, setDieren] = useState<number[]>([]);
  const [nieuwDier, setNieuwDier] = useState(false);
  // Na een dier van buitenaf: de dierenlijst opnieuw laden (de stap opnieuw opbouwen haalt hem vers op).
  const [dierenStand, setDierenStand] = useState(0);
  const [dierMelding, setDierMelding] = useState<string | null>(null);
  const [periode, setPeriode] = useState<Periode>({ aankomst: "", aankomstTijd: "10:00", vertrek: "", vertrekTijd: "16:00" });
  const [verblijf, setVerblijf] = useState<Record<number, DierVerblijf>>({});
  const [extras, setExtras] = useState<Extras>({});
  const [keuzes, setKeuzes] = useState<Keuzes>({ annuleringsverzekering: false, producten: [], overigeWensen: "", interneNotitie: "" });
  const [antwoorden, setAntwoorden] = useState<Antwoorden>({});

  // Een andere locatie heeft eigen verblijftypen, tarieven en arrangementen: wat daarvoor gekozen was, bestaat daar
  // niet per se. De periode en de dieren blijven wel staan; die hangen niet aan een vestiging.
  const vorigeLocatie = useRef<number | null>(null);
  useEffect(() => {
    if (locatie == null) return;
    const vorige = vorigeLocatie.current;
    vorigeLocatie.current = locatie;
    if (vorige != null && vorige !== locatie) {
      setVerblijf({});
      setExtras({});
    }
  }, [locatie]);

  // Geen arrangementen voor deze dieren of voor de reservering: dan is er bij Arrangementen &amp; extra's niets te
  // kiezen en slaat de wizard die stap over (Maarten, 2026-10-14). Behalve als de vestiging er eigen vragen in heeft
  // gezet; die blijven gesteld. Zolang de lijst nog niet binnen is blijft de stap staan, zodat de stappen niet
  // verspringen terwijl iemand ernaar kijkt.
  const klantData = useCustom<{ dieren?: PensionDier[] }>({
    url: `pension/klanten/${klant?.id ?? 0}`, method: "get", queryOptions: { enabled: klant != null },
  });
  const arrangementen = useCustom<Arrangement[]>({
    url: metLocatie(arrangementenUrl(periode.aankomst), locatie), method: "get",
    queryOptions: { enabled: klant != null },
  });
  const zonderArrangementen = useMemo(() => {
    if (!arrangementen.isSuccess || !klantData.isSuccess) return false;
    const stapA = indeling.stappen.find((s) => s.soort === "ARRANGEMENTEN");
    const eigen = rondBlok(stapA, "ARRANGEMENTEN");
    if (eigen.voor.length > 0 || eigen.na.length > 0) return false;
    const alle = Array.isArray(arrangementen.data?.data) ? arrangementen.data.data : [];
    const gekozenDieren = (klantData.data?.data?.dieren ?? []).filter((d) => dieren.includes(d.id));
    return !heeftArrangementen(alle, gekozenDieren);
  }, [arrangementen.isSuccess, arrangementen.data, klantData.isSuccess, klantData.data, indeling, dieren]);
  /** De indeling zoals hij nu loopt: de arrangementenstap uit als er niets te kiezen is. */
  const loopIndeling = useMemo(
    () => (zonderArrangementen
      ? { ...indeling, stappen: indeling.stappen.map((s) => (s.soort === "ARRANGEMENTEN" ? { ...s, actief: false } : s)) }
      : indeling),
    [indeling, zonderArrangementen],
  );
  const stappen = useMemo(() => loopIndeling.stappen.filter((s) => s.actief), [loopIndeling]);
  /** Na het aanmaken: het nummer, en of het een concept was. */
  const [resultaat, setResultaat] = useState<{ id: number; nummer: number | null; concept: boolean } | null>(null);
  const [zoekt, setZoekt] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSluit();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  }, [onSluit]);

  // Waar de wizard is: de stap met de gekozen sleutel, anders het begin (met een bekende klant: de dieren).
  const gevonden = stapId == null ? -1 : stappen.findIndex((s) => s.id === stapId);
  const begin = vooraf ? Math.max(0, stappen.findIndex((s) => s.soort === "DIEREN")) : 0;
  const plek = gevonden >= 0 ? gevonden : begin;
  const stap: WizardStap | undefined = stappen[plek];
  const naarPlek = (i: number) => {
    const doel = stappen[Math.min(Math.max(i, 0), stappen.length - 1)];
    if (doel) setStapId(doel.id);
  };
  const naar = (soort: StapSoort) => naarPlek(stappen.findIndex((s) => s.soort === soort));
  const volgende = () => naarPlek(plek + 1);
  const vorige = () => naarPlek(plek - 1);
  const na = stappen[plek + 1];
  const volgendeLabel = na ? t("nieuweReservering.volgendeNaar", { args: [stapNaam(t, na)] }) : undefined;
  const wizard = { versie, indeling: loopIndeling, antwoorden };

  const zoek = async () => {
    setZoekt(true);
    setFout(null);
    const params = new URLSearchParams();
    if (email.trim()) params.set("email", email.trim());
    if (adres.trim()) params.set("adres", adres.trim());
    try {
      const res = await dataProvider().custom!<GevondenKlant[]>({ url: `pension/klanten/zoeken?${params}`, method: "get" });
      const lijst = Array.isArray(res.data) ? res.data : [];
      setGezocht([email.trim(), adres.trim()].filter(Boolean).join(" · "));
      setResultaten(lijst);
      setKeuze(null);
      setKlantScherm(lijst.length === 0 ? "nieuw" : "lijst");
      naar("KIEZEN");
    } catch {
      setFout(t("nieuweReservering.zoeken.fout"));
    } finally {
      setZoekt(false);
    }
  };

  const klantKlaar = (gekozen: GekozenKlant) => {
    if (klant?.id !== gekozen.id) setDieren([]);
    setKlant(gekozen);
    setKlantScherm("lijst");
    setNieuwDier(false);
    naar("DIEREN");
  };
  const andereKlant = () => {
    setNieuwDier(false);
    setKlantScherm("lijst");
    naar(resultaten.length > 0 ? "KIEZEN" : "ZOEKEN");
  };
  const bekijkKlant = () => {
    setNieuwDier(false);
    setKlantScherm("controleren");
    naar("KIEZEN");
  };
  const naarLijst = () => (resultaten.length > 0 ? setKlantScherm("lijst") : naar("ZOEKEN"));
  /** De klant is op het controlescherm bijgewerkt: overal tonen wat er nu staat. */
  const klantBijgewerkt = (bij: GekozenKlant, adres: AdresDelen) => {
    setKlant(bij);
    setKeuze((x) => (x && x.id === bij.id ? { ...x, naam: bij.naam, email: bij.email, telefoon: bij.telefoon, ...adres } : x));
  };

  const soort = stap?.soort;
  let inhoud: ReactNode = null;
  if (soort === "ZOEKEN") {
    inhoud = (
      <Zoeken email={email} adres={adres} setEmail={setEmail} setAdres={setAdres} zoekt={zoekt} fout={fout} onZoek={zoek} t={t} stap={stap} />
    );
  } else if (soort === "KIEZEN" && klantScherm === "nieuw") {
    inhoud = <NieuweKlant gezocht={{ email, adres }} t={t} onTerug={naarLijst} onAnnuleer={onSluit} onAangemaakt={klantKlaar} uit={voorbeeld} />;
  } else if (soort === "KIEZEN" && klantScherm === "controleren") {
    const teControleren: GekozenKlant | null = keuze
      ? {
          id: keuze.id,
          naam: keuze.naam,
          adres: adresRegel(keuze),
          email: keuze.email,
          telefoon: keuze.telefoon,
          dieren: keuze.dieren,
          pensionBoekingen: keuze.pensionBoekingen,
        }
      : klant;
    inhoud = teControleren ? (
      <Controleren
        klant={teControleren}
        t={t}
        onTerug={naarLijst}
        onVolgende={() => klantKlaar(teControleren)}
        onGewijzigd={klantBijgewerkt}
      />
    ) : null;
  } else if (soort === "KIEZEN") {
    inhoud = (
      <Kiezen
        gezocht={gezocht}
        resultaten={resultaten}
        keuze={keuze}
        onKies={setKeuze}
        onNieuw={() => setKlantScherm("nieuw")}
        onTerug={() => naar("ZOEKEN")}
        onVolgende={() => setKlantScherm("controleren")}
        t={t}
        stap={stap}
      />
    );
  } else if (!klant || !stap) {
    inhoud = null;
  } else if (soort === "DIEREN" && nieuwDier) {
    inhoud = (
      <NieuwDier
        klant={klant}
        ctx={ctx}
        uit={voorbeeld}
        onAndereKlant={andereKlant}
        onTerug={() => setNieuwDier(false)}
        onToegevoegd={(id, melding) => {
          setDieren((x) => (x.includes(id) ? x : [...x, id]));
          setDierMelding(melding);
          setNieuwDier(false);
        }}
      />
    );
  } else if (soort === "DIEREN") {
    inhoud = (
      <DierenKiezen
        key={dierenStand}
        klant={klant}
        gekozen={dieren}
        setGekozen={setDieren}
        ctx={ctx}
        melding={dierMelding}
        onNieuwDier={() => {
          setDierMelding(null);
          if (nieuwDierExtern) {
            void nieuwDierExtern().then((id) => {
              if (id != null) setDieren((x) => (x.includes(id) ? x : [...x, id]));
              setDierenStand((n) => n + 1);
            });
            return;
          }
          setNieuwDier(true);
        }}
        onKlantWijzigen={andereKlant}
        onTerug={bekijkKlant}
        onVolgende={volgende}
        stap={stap}
        antwoorden={antwoorden}
        setAntwoorden={setAntwoorden}
      />
    );
  } else if (soort === "PERIODE") {
    inhoud = (
      <PeriodeKiezen
        klant={klant}
        dierIds={dieren}
        periode={periode}
        setPeriode={setPeriode}
        ctx={ctx}
        onKlant={bekijkKlant}
        onDieren={() => naar("DIEREN")}
        onTerug={vorige}
        onVolgende={volgende}
        stap={stap}
        antwoorden={antwoorden}
        setAntwoorden={setAntwoorden}
      />
    );
  } else if (soort === "VERBLIJF") {
    inhoud = (
      <VerblijfStap
        klant={klant}
        dierIds={dieren}
        setDierIds={setDieren}
        periode={periode}
        verblijf={verblijf}
        setVerblijf={setVerblijf}
        ctx={ctx}
        onKlant={bekijkKlant}
        onPeriode={() => naar("PERIODE")}
        onTerug={vorige}
        onVolgende={volgende}
        volgendeLabel={volgendeLabel}
        stap={stap}
        antwoorden={antwoorden}
        setAntwoorden={setAntwoorden}
      />
    );
  } else if (soort === "ARRANGEMENTEN") {
    inhoud = (
      <ArrangementenStap
        klant={klant}
        dierIds={dieren}
        periode={periode}
        extras={extras}
        setExtras={setExtras}
        ctx={ctx}
        onKlant={bekijkKlant}
        onTerug={vorige}
        onVolgende={volgende}
        stap={stap}
        antwoorden={antwoorden}
        setAntwoorden={setAntwoorden}
      />
    );
  } else if (soort === "KEUZES") {
    inhoud = (
      <KeuzesStap
        klant={klant}
        dierIds={dieren}
        periode={periode}
        verblijf={verblijf}
        extras={extras}
        keuzes={keuzes}
        setKeuzes={setKeuzes}
        ctx={ctx}
        onKlant={bekijkKlant}
        onTerug={vorige}
        onVolgende={volgende}
        volgendeLabel={volgendeLabel}
        wizard={wizard}
        setAntwoorden={setAntwoorden}
        stap={stap}
      />
    );
  } else if (soort === "CONTROLE" && resultaat) {
    inhoud = <Klaar resultaat={resultaat} ctx={ctx} onSluit={onSluit} />;
  } else if (soort === "CONTROLE") {
    inhoud = (
      <ControleStap
        klant={klant}
        dierIds={dieren}
        periode={periode}
        verblijf={verblijf}
        extras={extras}
        keuzes={keuzes}
        ctx={ctx}
        onKlant={bekijkKlant}
        onStap={setStapId}
        onTerug={vorige}
        onAangemaakt={setResultaat}
        wizard={wizard}
        setAntwoorden={setAntwoorden}
        stap={stap}
        uit={voorbeeld}
      />
    );
  } else if (soort === "EIGEN") {
    inhoud = (
      <EigenStap
        klant={klant}
        stap={stap}
        dierIds={dieren}
        antwoorden={antwoorden}
        setAntwoorden={setAntwoorden}
        ctx={ctx}
        onKlant={bekijkKlant}
        onTerug={vorige}
        onVolgende={volgende}
      />
    );
  }

  return createPortal(
    <LocatieContext.Provider value={locatieKeuze}>
    <div className="nr-overlay">
      <div className="nr" data-groot={groot || undefined} role="dialog" aria-modal="true" aria-labelledby="nr-titel">
        <header className="nr__kop">
          <span className="nr__icoon" aria-hidden="true">
            <CalendarDays size={26} />
          </span>
          <div className="nr__titels">
            <h2 id="nr-titel">{t("nieuweReservering.titel")}</h2>
            <p>{t("nieuweReservering.subtitel")}</p>
          </div>
          <button
            type="button"
            className="nr__kopknop"
            aria-label={groot ? t("nieuweReservering.klein") : t("nieuweReservering.groot")}
            onClick={() => setGroot((x) => !x)}
          >
            {groot ? <Minimize2 size={19} aria-hidden="true" /> : <Maximize2 size={19} aria-hidden="true" />}
          </button>
          <button type="button" className="nr__kopknop" aria-label={t("nieuweReservering.sluiten")} onClick={onSluit}>
            <X size={21} aria-hidden="true" />
          </button>
        </header>
        {voorbeeld && (
          <p className="nr-voorbeeld" role="note">
            <Sparkles size={16} aria-hidden="true" />
            {t("nieuweReservering.voorbeeld")}
          </p>
        )}
        <ol className="nr-stappen" aria-label={t("nieuweReservering.stappen")}>
          {stappen.map((s, i) => (
            <li
              key={s.id}
              className="nr-stap"
              data-staat={i < plek ? "klaar" : i === plek ? "nu" : "straks"}
              aria-current={i === plek ? "step" : undefined}
            >
              <span className="nr-stap__bol" aria-hidden="true">
                {i < plek ? <Check size={16} strokeWidth={3} /> : i + 1}
              </span>
              <span className="nr-stap__naam">
                {s.soort === "DIEREN" && i === plek && nieuwDier ? t("nieuweReservering.stap.nieuwDier") : stapNaam(t, s)}
              </span>
            </li>
          ))}
        </ol>
        {inhoud}
      </div>
    </div>
    </LocatieContext.Provider>,
    document.body,
  );
}

// ── Bouwstenen ───────────────────────────────────────────────────────────────────────────────────────

// ── Stap 1: klant zoeken ─────────────────────────────────────────────────────────────────────────────

function Zoeken({
  email,
  adres,
  setEmail,
  setAdres,
  zoekt,
  fout,
  onZoek,
  t,
  stap = ZOEK_STANDAARD,
}: {
  stap?: WizardStap;
  email: string;
  adres: string;
  setEmail: (v: string) => void;
  setAdres: (v: string) => void;
  zoekt: boolean;
  fout: string | null;
  onZoek: () => void;
  t: Vertaal;
}) {
  const elementen = stap.elementen.filter((e) => e.actief);
  const emailAan = elementen.some((e) => e.blok === "EMAIL");
  const adresAan = elementen.some((e) => e.blok === "ADRES");
  const kan = (emailAan && email.trim().length >= 3) || (adresAan && adres.replace(/[\s,]/g, "").length >= 2);
  const kop = stapKop(t, stap, "zoeken");
  return (
    <form
      className="nr__vorm"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (kan && !zoekt) onZoek();
      }}
    >
      <div className="nr__inhoud">
        <section className="nr-kaart">
          <h3 className="nr-kaart__titel">{kop.titel}</h3>
          <p className="nr-kaart__uitleg">{kop.uitleg}</p>
          {elementen.map((e, i) => {
            if (e.soort === "TEKST") return <TekstBlok key={e.id} titel={e.titel} tekst={e.uitleg} />;
            if (e.blok !== "EMAIL" && e.blok !== "ADRES") return null;
            const isEmail = e.blok === "EMAIL";
            const eerder = elementen.slice(0, i).some((x) => x.blok === "EMAIL" || x.blok === "ADRES");
            return (
              <Fragment key={e.id}>
                {eerder && (
                  <div className="nr-scheiding">
                    <span>{t("nieuweReservering.zoeken.enOf")}</span>
                  </div>
                )}
                <div className="nr-zoekblok">
                  <label className="nr-zoekblok__kop" htmlFor={isEmail ? "nr-zoek-email" : "nr-zoek-adres"}>
                    <span className="nr-rond" aria-hidden="true">
                      {isEmail ? <Mail size={20} /> : <Home size={20} />}
                    </span>
                    {blokTitel(t, "ZOEKEN", e)}
                  </label>
                  {isEmail ? (
                    <input
                      id="nr-zoek-email"
                      className="nr-invoer"
                      type="text"
                      inputMode="email"
                      autoComplete="off"
                      placeholder={e.uitleg ?? t("nieuweReservering.zoeken.emailPlaats")}
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      autoFocus={!eerder}
                    />
                  ) : (
                    <AdresZoeker
                      id="nr-zoek-adres"
                      waarde={adres}
                      onWaarde={setAdres}
                      plaatshouder={e.uitleg ?? t("nieuweReservering.zoeken.adresPlaats")}
                      t={t}
                    />
                  )}
                </div>
              </Fragment>
            );
          })}
          {fout && (
            <p className="nr-melding" role="alert">
              {fout}
            </p>
          )}
        </section>
      </div>
      <Voet
        links={<Terug t={t} disabled />}
        rechts={
          <button type="submit" className="nr-knop nr-knop--hoofd" disabled={!kan || zoekt}>
            {t("nieuweReservering.zoeken.knop")}
            <ChevronRight size={17} aria-hidden="true" />
          </button>
        }
      />
    </form>
  );
}

// ── Stap 2: klant kiezen ─────────────────────────────────────────────────────────────────────────────

function Kiezen({
  gezocht,
  resultaten,
  keuze,
  onKies,
  onNieuw,
  onTerug,
  onVolgende,
  t,
  stap,
}: {
  stap?: WizardStap;
  gezocht: string;
  resultaten: GevondenKlant[];
  keuze: GevondenKlant | null;
  onKies: (k: GevondenKlant) => void;
  onNieuw: () => void;
  onTerug: () => void;
  onVolgende: () => void;
  t: Vertaal;
}) {
  return (
    <>
      <div className="nr__inhoud">
        <section className="nr-kaart">
          <h3 className="nr-kaart__titel">{stapKop(t, stap, "kiezen").titel}</h3>
          <p className="nr-kaart__uitleg">{stapKop(t, stap, "kiezen").uitleg}</p>
          <p className="nr-zoekterm">
            <Search size={17} aria-hidden="true" />
            {t("nieuweReservering.kiezen.voor")} <strong>{gezocht}</strong>
          </p>
          <ul className="nr-klanten">
            {resultaten.map((k) => {
              const gekozen = keuze?.id === k.id;
              const regel = adresRegel(k);
              return (
                <li key={k.id} className="nr-klant" data-gekozen={gekozen || undefined}>
                  <span className="nr-klant__avatar" aria-hidden="true">
                    {initialen(k.naam)}
                  </span>
                  <div className="nr-klant__tekst">
                    <strong className="nr-klant__naam">
                      {k.naam}
                      {!k.actief && <span className="nr-chip">{t("nieuweReservering.kiezen.inactief")}</span>}
                    </strong>
                    {regel && (
                      <span className="nr-regel">
                        <MapPin size={15} aria-hidden="true" />
                        {regel}
                      </span>
                    )}
                    {(k.email || k.telefoon) && (
                      <span className="nr-regel">
                        {k.email && (
                          <>
                            <Mail size={15} aria-hidden="true" />
                            {k.email}
                          </>
                        )}
                        {k.email && k.telefoon && <span className="nr-punt">•</span>}
                        {k.telefoon && (
                          <>
                            <Phone size={15} aria-hidden="true" />
                            {k.telefoon}
                          </>
                        )}
                      </span>
                    )}
                    <span className="nr-regel">
                      <PawPrint size={15} aria-hidden="true" />
                      {k.dieren === 1
                        ? t("nieuweReservering.kiezen.dierEen")
                        : t("nieuweReservering.kiezen.dieren", { args: [k.dieren] })}
                      <span className="nr-punt">•</span>
                      <CalendarDays size={15} aria-hidden="true" />
                      {k.pensionBoekingen === 1
                        ? t("nieuweReservering.kiezen.boekingEen")
                        : t("nieuweReservering.kiezen.boekingen", { args: [k.pensionBoekingen] })}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="nr-knop nr-knop--rand"
                    aria-pressed={gekozen}
                    aria-label={t("nieuweReservering.kiezen.selecteerAria", { args: [k.naam] })}
                    onClick={() => onKies(k)}
                  >
                    {gekozen && <Check size={16} aria-hidden="true" />}
                    {gekozen ? t("nieuweReservering.kiezen.geselecteerd") : t("nieuweReservering.kiezen.selecteren")}
                  </button>
                </li>
              );
            })}
          </ul>
          {resultaten.length >= MAX_GEVONDEN && <p className="nr-kaart__uitleg">{t("nieuweReservering.kiezen.meer")}</p>}
          {eigenElementen(stap).map((e) => (
            <TekstBlok key={e.id} titel={e.titel} tekst={e.uitleg} />
          ))}
          <div className="nr-nieuw">
            <span className="nr-rond nr-rond--groot" aria-hidden="true">
              <UserPlus size={24} />
            </span>
            <div className="nr-nieuw__tekst">
              <strong>{t("nieuweReservering.kiezen.nietGevonden")}</strong>
              <span>{t("nieuweReservering.kiezen.nietGevondenUitleg")}</span>
            </div>
            <button type="button" className="nr-knop nr-knop--rand" onClick={onNieuw}>
              <Plus size={17} aria-hidden="true" />
              {t("nieuweReservering.kiezen.nieuw")}
            </button>
          </div>
        </section>
      </div>
      <Voet links={<Terug t={t} onClick={onTerug} />} rechts={<Volgende t={t} disabled={!keuze} onClick={onVolgende} />} />
    </>
  );
}

// ── Stap 3: klant controleren of aanmaken ────────────────────────────────────────────────────────────

/** De gegevens van de klant zoals GET /api/relaties/{id} ze geeft; PUT stuurt dezelfde velden terug. */
interface RelatieGegevens {
  id: number;
  klantnaam: string | null;
  version: number | null;
  aanhef: string | null;
  voorletters: string | null;
  tussenvoegsel: string | null;
  achternaam: string | null;
  naamBedrijf: string | null;
  emailAdres: string | null;
  telefoon1: string | null;
  telefoon2: string | null;
  straat: string | null;
  huisnummer: string | null;
  huisnummerToevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
}

/** De adresdelen van een klant, zoals de zoekresultaten ze dragen. */
export interface AdresDelen {
  straat: string | null;
  huisnummer: string | null;
  toevoeging: string | null;
  postcode: string | null;
  woonplaats: string | null;
}

/**
 * Stap Klant kiezen, scherm Klant controleren: de gegevens van de klant, en ze hier meteen aanpassen of
 * aanvullen — een ontbrekend e-mailadres of telefoonnummer hoef je dus niet eerst op het klantscherm te
 * gaan invullen. Opslaan gaat naar PUT /api/relaties/{id} met de versie erbij: wijzigt iemand anders de
 * klant ondertussen, dan zegt het scherm dat en laadt het de gegevens opnieuw.
 */
function Controleren({
  klant,
  t,
  onTerug,
  onVolgende,
  onGewijzigd,
}: {
  klant: GekozenKlant;
  t: Vertaal;
  onTerug: () => void;
  onVolgende: () => void;
  onGewijzigd: (klant: GekozenKlant, adres: AdresDelen) => void;
}) {
  const query = useCustom<RelatieGegevens>({ url: `relaties/${klant.id}`, method: "get" });
  const detail = query.data?.data;
  const [f, setF] = useState<RelatieGegevens | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const { mutateAsync, isLoading } = useCustomMutation<RelatieGegevens>();
  const zet = (deel: Partial<RelatieGegevens>) => setF((x) => (x ? { ...x, ...deel } : x));
  const waarde = (v: string | null | undefined) => v ?? "";
  const leeg = (v: string | null | undefined) => (v == null || v.trim() === "" ? null : v.trim());
  const organisatie = !!detail?.naamBedrijf;

  const rijen: [string, ReactNode][] = [
    [t("nieuweReservering.controleren.naam"), detail?.klantnaam ?? klant.naam],
    [t("nieuweReservering.controleren.adres"), adresRegelVan(detail) ?? klant.adres ?? "—"],
    [t("nieuweReservering.nieuw.email"), detail?.emailAdres ?? klant.email ?? "—"],
    [t("nieuweReservering.nieuw.telefoon"), detail?.telefoon1 ?? klant.telefoon ?? "—"],
  ];
  if (detail?.telefoon2) rijen.push([t("nieuweReservering.controleren.telefoon2"), detail.telefoon2]);
  if (klant.dieren != null) rijen.push([t("nieuweReservering.controleren.dieren"), klant.dieren]);
  if (klant.pensionBoekingen != null) rijen.push([t("nieuweReservering.controleren.boekingen"), klant.pensionBoekingen]);

  const opslaan = async () => {
    if (!f) return;
    setFout(null);
    try {
      const res = await mutateAsync({
        url: `relaties/${f.id}`,
        method: "put",
        values: {
          aanhef: leeg(f.aanhef),
          voorletters: leeg(f.voorletters),
          tussenvoegsel: leeg(f.tussenvoegsel),
          achternaam: leeg(f.achternaam),
          bedrijfsnaam: leeg(f.naamBedrijf),
          straat: leeg(f.straat),
          huisnummer: leeg(f.huisnummer),
          huisnummerToevoeging: leeg(f.huisnummerToevoeging),
          postcode: leeg(f.postcode),
          woonplaats: leeg(f.woonplaats),
          land: leeg(f.land),
          email: leeg(f.emailAdres),
          telefoon1: leeg(f.telefoon1),
          telefoon2: leeg(f.telefoon2),
          version: f.version,
        },
        successNotification: false,
        errorNotification: false,
      });
      const g = res.data;
      onGewijzigd(
        {
          ...klant,
          naam: g.klantnaam ?? klant.naam,
          adres: adresRegelVan(g) ?? klant.adres,
          email: g.emailAdres,
          telefoon: g.telefoon1,
        },
        {
          straat: g.straat,
          huisnummer: g.huisnummer,
          toevoeging: g.huisnummerToevoeging,
          postcode: g.postcode,
          woonplaats: g.woonplaats,
        },
      );
      setF(null);
      setOpgeslagen(true);
      void query.refetch();
    } catch (e) {
      const status = (e as { statusCode?: number } | null)?.statusCode;
      setFout(t(`nieuweReservering.controleren.${status === 403 ? "geenRecht" : status === 409 ? "conflict" : "fout"}`));
      if (status === 409) {
        setF(null);
        void query.refetch();
      }
    }
  };

  return (
    <>
      <div className="nr__inhoud">
        <section className="nr-kaart">
          <h3 className="nr-kaart__titel">{t("nieuweReservering.controleren.titel")}</h3>
          <p className="nr-kaart__uitleg">{t("nieuweReservering.controleren.uitleg")}</p>
          {f ? (
            <>
              {organisatie ? (
                <div className="nr-rij nr-rij--een">
                  <Veld label={t("nieuweReservering.nieuw.bedrijfsnaam")}>
                    <input className="nr-invoer" maxLength={100} value={waarde(f.naamBedrijf)} onChange={(e) => zet({ naamBedrijf: e.target.value })} />
                  </Veld>
                </div>
              ) : (
                <div className="nr-rij nr-rij--persoon">
                  <Veld label={t("nieuweReservering.nieuw.voorletters")}>
                    <input className="nr-invoer" maxLength={10} value={waarde(f.voorletters)} onChange={(e) => zet({ voorletters: e.target.value })} />
                  </Veld>
                  <Veld label={t("nieuweReservering.nieuw.tussenvoegsel")}>
                    <input className="nr-invoer" maxLength={10} value={waarde(f.tussenvoegsel)} onChange={(e) => zet({ tussenvoegsel: e.target.value })} />
                  </Veld>
                  <Veld label={t("nieuweReservering.nieuw.achternaam")}>
                    <input className="nr-invoer" maxLength={50} value={waarde(f.achternaam)} onChange={(e) => zet({ achternaam: e.target.value })} />
                  </Veld>
                </div>
              )}
              <div className="nr-rij nr-rij--twee">
                <Veld label={t("nieuweReservering.nieuw.email")}>
                  <input className="nr-invoer" type="email" maxLength={250} value={waarde(f.emailAdres)} onChange={(e) => zet({ emailAdres: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.nieuw.telefoon")}>
                  <input className="nr-invoer" type="tel" maxLength={25} value={waarde(f.telefoon1)} onChange={(e) => zet({ telefoon1: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.controleren.telefoon2")}>
                  <input className="nr-invoer" type="tel" maxLength={25} value={waarde(f.telefoon2)} onChange={(e) => zet({ telefoon2: e.target.value })} />
                </Veld>
              </div>
              <h4 className="nr-sectie">{t("nieuweReservering.nieuw.adres")}</h4>
              <div className="nr-rij nr-rij--een">
                <Veld label={t("nieuweReservering.nieuw.adresZoeken")}>
                  <AdresZoeker
                    waarde={adresTerm(f)}
                    onWaarde={(v) => zet({ straat: v })}
                    plaatshouder={t("nieuweReservering.nieuw.adresZoekenPlaats")}
                    t={t}
                    onAdres={(a) =>
                      zet({
                        straat: a.street ?? f.straat,
                        huisnummer: a.buildingNumber ?? f.huisnummer,
                        huisnummerToevoeging: a.buildingNumberAddition ?? f.huisnummerToevoeging,
                        postcode: a.postcode ?? f.postcode,
                        woonplaats: a.city ?? f.woonplaats,
                      })
                    }
                  />
                </Veld>
              </div>
              <div className="nr-rij nr-rij--adres">
                <Veld label={t("nieuweReservering.nieuw.straat")}>
                  <input className="nr-invoer" maxLength={100} value={waarde(f.straat)} onChange={(e) => zet({ straat: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.nieuw.huisnummer")}>
                  <input className="nr-invoer" maxLength={20} value={waarde(f.huisnummer)} onChange={(e) => zet({ huisnummer: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.nieuw.toevoeging")}>
                  <input className="nr-invoer" maxLength={50} value={waarde(f.huisnummerToevoeging)} onChange={(e) => zet({ huisnummerToevoeging: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.nieuw.postcode")}>
                  <input className="nr-invoer" maxLength={7} value={waarde(f.postcode)} onChange={(e) => zet({ postcode: e.target.value })} />
                </Veld>
                <Veld label={t("nieuweReservering.nieuw.plaats")}>
                  <input className="nr-invoer" maxLength={50} value={waarde(f.woonplaats)} onChange={(e) => zet({ woonplaats: e.target.value })} />
                </Veld>
              </div>
              <div className="nr-controleren__knoppen">
                <button type="button" className="nr-knop nr-knop--rand" disabled={isLoading} onClick={() => setF(null)}>
                  {t("nieuweReservering.annuleren")}
                </button>
                <button type="button" className="nr-knop nr-knop--hoofd" disabled={isLoading} onClick={() => void opslaan()}>
                  <Check size={16} aria-hidden="true" />
                  {t("nieuweReservering.controleren.opslaan")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="nr-controle">
                <span className="nr-klant__avatar nr-klant__avatar--groot" aria-hidden="true">
                  {initialen(detail?.klantnaam ?? klant.naam)}
                </span>
                <dl className="nr-gegevens">
                  {rijen.map(([label, tekstwaarde]) => (
                    <div key={label} className="nr-gegevens__rij">
                      <dt>{label}</dt>
                      <dd>{tekstwaarde}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="nr-controleren__knoppen">
                <p className="nr-kaart__uitleg">{t("nieuweReservering.controleren.klopt")}</p>
                <button
                  type="button"
                  className="nr-knop nr-knop--rand"
                  disabled={!detail}
                  onClick={() => {
                    setOpgeslagen(false);
                    setFout(null);
                    if (detail) setF(detail);
                  }}
                >
                  <Pencil size={16} aria-hidden="true" />
                  {t("nieuweReservering.controleren.aanpassen")}
                </button>
              </div>
            </>
          )}
          {query.isError && <p className="nr-melding">{t("nieuweReservering.controleren.laadFout")}</p>}
          {fout && (
            <p className="nr-melding" role="alert">
              {fout}
            </p>
          )}
          {opgeslagen && !f && (
            <p className="nr-kaart__uitleg" role="status">
              {t("nieuweReservering.controleren.opgeslagen")}
            </p>
          )}
        </section>
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} disabled={!!f} />}
        rechts={<Volgende t={t} onClick={onVolgende} disabled={!!f} />}
      />
    </>
  );
}

/** "Hogenhof 7, 7581 WL Losser" uit de losse velden; leeg als er geen adres is. */
function adresRegelVan(g: RelatieGegevens | undefined): string | null {
  if (!g) return null;
  return adresRegel({
    straat: g.straat,
    huisnummer: g.huisnummer,
    toevoeging: g.huisnummerToevoeging,
    postcode: g.postcode,
    woonplaats: g.woonplaats,
  });
}

/** Wat er in het zoekveld staat: het adres zoals het nu is ingevuld. */
function adresTerm(g: RelatieGegevens): string {
  return adresRegelVan(g) ?? "";
}

interface KlantFormulier {
  organisatie: boolean;
  bedrijfsnaam: string;
  voorletters: string;
  tussenvoegsel: string;
  achternaam: string;
  geslacht: "" | "MAN" | "VROUW";
  geboortedatum: string;
  email: string;
  telefoon: string;
  straat: string;
  huisnummer: string;
  toevoeging: string;
  postcode: string;
  woonplaats: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function foutTekst(t: Vertaal, fout: unknown, prefix: string): string {
  const status = (fout as { statusCode?: number } | null)?.statusCode;
  return t(`${prefix}.${status === 403 ? "geenRecht" : status === 400 ? "ongeldig" : "fout"}`);
}

function NieuweKlant({
  gezocht,
  t,
  onTerug,
  onAnnuleer,
  onAangemaakt,
  uit,
}: {
  uit?: boolean;
  gezocht: { email: string; adres: string };
  t: Vertaal;
  onTerug: () => void;
  onAnnuleer: () => void;
  onAangemaakt: (klant: GekozenKlant) => void;
}) {
  const [f, setF] = useState<KlantFormulier>(() => ({
    organisatie: false,
    bedrijfsnaam: "",
    voorletters: "",
    tussenvoegsel: "",
    achternaam: "",
    geslacht: "",
    geboortedatum: "",
    email: gezocht.email.includes("@") ? gezocht.email.trim() : "",
    telefoon: "",
    straat: "",
    huisnummer: uitAdres(gezocht.adres).huisnummer,
    toevoeging: "",
    postcode: uitAdres(gezocht.adres).postcode,
    woonplaats: "",
  }));
  const [geprobeerd, setGeprobeerd] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [zoektAdres, setZoektAdres] = useState(false);
  /** Het adresveld met de suggesties van Postcode.eu, vooringevuld met wat er gezocht is. */
  const [adresTerm, setAdresTerm] = useState(gezocht.adres);
  const { mutateAsync, isLoading } = useCustomMutation<{ id: number; naam: string }>();
  const zet = <K extends keyof KlantFormulier>(k: K, v: KlantFormulier[K]) => setF((x) => ({ ...x, [k]: v }));

  const ontbreekt = [
    f.organisatie && !f.bedrijfsnaam.trim(),
    !f.organisatie && !f.voorletters.trim(),
    !f.organisatie && !f.achternaam.trim(),
    !f.straat.trim(),
    !f.huisnummer.trim(),
    !f.postcode.trim(),
    !f.woonplaats.trim(),
  ].some(Boolean);
  const emailFout = f.email.trim() !== "" && !EMAIL.test(f.email.trim());
  const leeg = (v: string) => (v.trim() === "" ? null : v.trim());

  const opzoeken = async () => {
    setZoektAdres(true);
    setFout(null);
    const gevonden = await lookupPostcode(f.postcode, f.huisnummer, f.toevoeging);
    setZoektAdres(false);
    if (gevonden && (gevonden.straat || gevonden.woonplaats)) {
      setF((x) => ({ ...x, straat: gevonden.straat ?? x.straat, woonplaats: gevonden.woonplaats ?? x.woonplaats }));
    } else {
      setFout(t("nieuweReservering.nieuw.adresNietGevonden"));
    }
  };

  const opslaan = async () => {
    setGeprobeerd(true);
    setFout(null);
    if (ontbreekt || emailFout) return;
    try {
      const res = await mutateAsync({
        url: "pension/klanten",
        method: "post",
        values: {
          organisatie: f.organisatie,
          bedrijfsnaam: f.organisatie ? leeg(f.bedrijfsnaam) : null,
          voorletters: leeg(f.voorletters),
          tussenvoegsel: leeg(f.tussenvoegsel),
          achternaam: leeg(f.achternaam),
          geslacht: f.geslacht === "" ? null : f.geslacht,
          geboortedatum: f.organisatie || f.geboortedatum === "" ? null : f.geboortedatum,
          email: leeg(f.email),
          telefoon: leeg(f.telefoon),
          straat: leeg(f.straat),
          huisnummer: leeg(f.huisnummer),
          toevoeging: leeg(f.toevoeging),
          postcode: leeg(f.postcode),
          woonplaats: leeg(f.woonplaats),
        },
        successNotification: false,
        errorNotification: false,
      });
      onAangemaakt({
        id: res.data.id,
        naam: res.data.naam,
        adres: adresRegel({
          straat: leeg(f.straat),
          huisnummer: leeg(f.huisnummer),
          toevoeging: leeg(f.toevoeging),
          postcode: leeg(f.postcode),
          woonplaats: leeg(f.woonplaats),
        }),
        email: leeg(f.email),
        telefoon: leeg(f.telefoon),
        dieren: 0,
        pensionBoekingen: 0,
      });
    } catch (e) {
      setFout(foutTekst(t, e, "nieuweReservering.nieuw"));
    }
  };

  const mist = (v: string) => geprobeerd && v.trim() === "";

  return (
    <form
      className="nr__vorm"
      noValidate
      aria-label={t("nieuweReservering.nieuw.titel")}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isLoading && !uit) void opslaan();
      }}
    >
      <div className="nr__inhoud">
        <section className="nr-kaart">
          <h3 className="nr-kaart__titel">{t("nieuweReservering.nieuw.titel")}</h3>
          <p className="nr-kaart__uitleg">{t("nieuweReservering.nieuw.uitleg")}</p>

          {f.organisatie && (
            <div className="nr-rij nr-rij--een">
              <Veld label={t("nieuweReservering.nieuw.bedrijfsnaam")} verplicht>
                <input
                  className="nr-invoer"
                  data-fout={mist(f.bedrijfsnaam) || undefined}
                  value={f.bedrijfsnaam}
                  onChange={(e) => zet("bedrijfsnaam", e.target.value)}
                />
              </Veld>
            </div>
          )}

          <h4 className="nr-sectie">
            {f.organisatie ? t("nieuweReservering.nieuw.contactpersoon") : t("nieuweReservering.nieuw.persoon")}
          </h4>
          <div className="nr-rij nr-rij--persoon">
            <Veld label={t("nieuweReservering.nieuw.voorletters")} verplicht={!f.organisatie}>
              <input
                className="nr-invoer"
                data-fout={(!f.organisatie && mist(f.voorletters)) || undefined}
                maxLength={10}
                value={f.voorletters}
                onChange={(e) => zet("voorletters", e.target.value)}
              />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.tussenvoegsel")}>
              <input className="nr-invoer" maxLength={10} value={f.tussenvoegsel} onChange={(e) => zet("tussenvoegsel", e.target.value)} />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.achternaam")} verplicht={!f.organisatie}>
              <input
                className="nr-invoer"
                data-fout={(!f.organisatie && mist(f.achternaam)) || undefined}
                maxLength={50}
                value={f.achternaam}
                onChange={(e) => zet("achternaam", e.target.value)}
              />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.geslacht")}>
              <select
                className="nr-invoer"
                value={f.geslacht}
                onChange={(e) => zet("geslacht", e.target.value as KlantFormulier["geslacht"])}
              >
                <option value="">{t("nieuweReservering.nieuw.geslachtLeeg")}</option>
                <option value="MAN">{t("nieuweReservering.nieuw.geslachtMAN")}</option>
                <option value="VROUW">{t("nieuweReservering.nieuw.geslachtVROUW")}</option>
              </select>
            </Veld>
            {!f.organisatie && (
              <Veld label={t("nieuweReservering.nieuw.geboortedatum")}>
                <DateField className="nr-invoer"
                  value={f.geboortedatum}
                  onChange={(e) => zet("geboortedatum", e.target.value)}
                />
              </Veld>
            )}
          </div>

          <h4 className="nr-sectie">{t("nieuweReservering.nieuw.contact")}</h4>
          <div className="nr-rij nr-rij--twee">
            <Veld label={t("nieuweReservering.nieuw.email")}>
              <input
                className="nr-invoer"
                type="email"
                data-fout={emailFout || undefined}
                maxLength={250}
                value={f.email}
                onChange={(e) => zet("email", e.target.value)}
              />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.telefoon")}>
              <input className="nr-invoer" type="tel" maxLength={25} value={f.telefoon} onChange={(e) => zet("telefoon", e.target.value)} />
            </Veld>
          </div>
          {emailFout && <p className="nr-melding">{t("nieuweReservering.nieuw.emailOngeldig")}</p>}

          <h4 className="nr-sectie">{t("nieuweReservering.nieuw.adres")}</h4>
          <div className="nr-rij nr-rij--een">
            <Veld label={t("nieuweReservering.nieuw.adresZoeken")}>
              <AdresZoeker
                waarde={adresTerm}
                onWaarde={setAdresTerm}
                plaatshouder={t("nieuweReservering.nieuw.adresZoekenPlaats")}
                t={t}
                onAdres={(a) =>
                  setF((x) => ({
                    ...x,
                    straat: a.street ?? x.straat,
                    huisnummer: a.buildingNumber ?? x.huisnummer,
                    toevoeging: a.buildingNumberAddition ?? x.toevoeging,
                    postcode: a.postcode ?? x.postcode,
                    woonplaats: a.city ?? x.woonplaats,
                  }))
                }
              />
            </Veld>
          </div>
          <div className="nr-rij nr-rij--adres">
            <Veld label={t("nieuweReservering.nieuw.straat")} verplicht>
              <input
                className="nr-invoer"
                data-fout={mist(f.straat) || undefined}
                maxLength={100}
                value={f.straat}
                onChange={(e) => zet("straat", e.target.value)}
              />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.huisnummer")} verplicht>
              <input
                className="nr-invoer"
                data-fout={mist(f.huisnummer) || undefined}
                maxLength={20}
                value={f.huisnummer}
                onChange={(e) => zet("huisnummer", e.target.value)}
              />
            </Veld>
            <Veld label={t("nieuweReservering.nieuw.toevoeging")}>
              <input className="nr-invoer" maxLength={50} value={f.toevoeging} onChange={(e) => zet("toevoeging", e.target.value)} />
            </Veld>
            <div className="nr-veld">
              <label className="nr-veld__label" htmlFor="nr-postcode">
                {t("nieuweReservering.nieuw.postcode")}
                <span className="nr-veld__ster" aria-hidden="true">
                  {" *"}
                </span>
              </label>
              <span className="nr-postcode">
                <input
                  id="nr-postcode"
                  className="nr-invoer"
                  data-fout={mist(f.postcode) || undefined}
                  maxLength={7}
                  value={f.postcode}
                  onChange={(e) => zet("postcode", e.target.value)}
                />
                <button
                  type="button"
                  className="nr-postcode__knop"
                  aria-label={t("nieuweReservering.nieuw.opzoeken")}
                  title={t("nieuweReservering.nieuw.opzoeken")}
                  disabled={zoektAdres || !f.postcode.trim() || !f.huisnummer.trim()}
                  onClick={() => void opzoeken()}
                >
                  <Search size={18} aria-hidden="true" />
                </button>
              </span>
            </div>
            <Veld label={t("nieuweReservering.nieuw.plaats")} verplicht>
              <input
                className="nr-invoer"
                data-fout={mist(f.woonplaats) || undefined}
                maxLength={50}
                value={f.woonplaats}
                onChange={(e) => zet("woonplaats", e.target.value)}
              />
            </Veld>
          </div>

          <label className="nr-vink">
            <input type="checkbox" checked={f.organisatie} onChange={(e) => zet("organisatie", e.target.checked)} />
            {t("nieuweReservering.nieuw.organisatie")}
          </label>
          {geprobeerd && ontbreekt && (
            <p className="nr-melding" role="alert">
              {t("nieuweReservering.nieuw.verplicht")}
            </p>
          )}
          {fout && (
            <p className="nr-melding" role="alert">
              {fout}
            </p>
          )}
        </section>
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} />}
        rechts={
          <>
            <button type="button" className="nr-knop nr-knop--rand" onClick={onAnnuleer}>
              {t("nieuweReservering.annuleren")}
            </button>
            <button type="submit" className="nr-knop nr-knop--hoofd" disabled={isLoading || uit}>
              {t("nieuweReservering.nieuw.opslaan")}
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          </>
        }
      />
    </form>
  );
}

// ── Stap 4: dieren selecteren ────────────────────────────────────────────────────────────────────────

/** Nu in pension, gepland, het laatste verblijf, of geen reservering. */
function verblijf(d: PensionDier, reserveringen: PensionReservering[], { t, locale }: Context): string {
  if (d.status === "IN_PENSION") return t("nieuweReservering.dieren.inPension");
  if (d.status === "KOMENDE_RESERVERING" && d.huidigeReservering) {
    return t("nieuweReservering.dieren.gepland", { args: [datum(d.huidigeReservering.van, locale)] });
  }
  const laatste = reserveringen
    .filter((r) => r.dierId === d.id && (r.status === "AFGEROND" || r.status === "IN_PENSION"))
    .map((r) => r.van)
    .sort();
  return laatste.length > 0
    ? t("nieuweReservering.dieren.laatste", { args: [datum(laatste[laatste.length - 1], locale)] })
    : t("nieuweReservering.dieren.geenReservering");
}

function DierenKiezen({
  klant,
  gekozen,
  setGekozen,
  ctx,
  melding,
  onNieuwDier,
  onKlantWijzigen,
  onTerug,
  onVolgende,
  stap,
  antwoorden = GEEN_ANTWOORDEN,
  setAntwoorden = NIETS,
}: {
  stap?: WizardStap;
  antwoorden?: Antwoorden;
  setAntwoorden?: Dispatch<SetStateAction<Antwoorden>>;
  klant: GekozenKlant;
  gekozen: number[];
  setGekozen: Dispatch<SetStateAction<number[]>>;
  ctx: Context;
  melding: string | null;
  onNieuwDier: () => void;
  onKlantWijzigen: () => void;
  onTerug: () => void;
  onVolgende: () => void;
}) {
  const t = ctx.t as Vertaal;
  const { dieren: alle, reserveringen, isLoading, isError } = useKlantDieren(klant.id);
  const fotos = useAnimalPhotoUrls(alle.map((d) => d.id));
  const [zoek, setZoek] = useState("");
  const [soort, setSoort] = useState<string | null>(null);
  const soorten = [...new Set(alle.map((d) => d.diersoort).filter((s): s is string => !!s))];
  const term = zoek.trim().toLowerCase();
  const zichtbaar = alle.filter(
    (d) => (soort == null || d.diersoort === soort) && (term === "" || (d.naam ?? "").toLowerCase().includes(term)),
  );
  const wissel = (id: number) => setGekozen((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));
  const verplicht = useVerplicht(stap, antwoorden, gekozen);
  const nieuwBlok = blokElement(stap, "NIEUW_DIER");
  const kop = stapKop(t, stap, "dieren");

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk
          klant={klant}
          label={t("nieuweReservering.dieren.klant")}
          knop={t("nieuweReservering.dieren.klantWijzigen")}
          knopIcoon={<Pencil size={16} aria-hidden="true" />}
          onKnop={onKlantWijzigen}
        />

        <h3 className="nr-kaart__titel">{kop.titel}</h3>
        <p className="nr-kaart__uitleg">{kop.uitleg}</p>

        <div className="nr-balk">
          <label className="nr-zoekveld">
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              aria-label={t("nieuweReservering.dieren.zoek")}
              placeholder={t("nieuweReservering.dieren.zoek")}
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
            />
          </label>
          {soorten.length > 1 && (
            <div className="nr-filters" role="group" aria-label={t("nieuweReservering.dieren.soort")}>
              <button type="button" className="nr-filter" aria-pressed={soort == null} onClick={() => setSoort(null)}>
                {t("nieuweReservering.dieren.alles")}
              </button>
              {soorten.map((s) => (
                <button key={s} type="button" className="nr-filter" aria-pressed={soort === s} onClick={() => setSoort(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}
          {blokAan(stap, "NIEUW_DIER") && (
            <button type="button" className="nr-knop nr-knop--rand nr-balk__nieuw" onClick={onNieuwDier}>
              <Plus size={17} aria-hidden="true" />
              {nieuwBlok ? blokTitel(t, "DIEREN", nieuwBlok) : t("nieuweReservering.dieren.nieuw")}
            </button>
          )}
        </div>

        {melding && (
          <p className="nr-melding" role="status">
            {melding}
          </p>
        )}
        {isLoading && <p className="nr-kaart__uitleg">{t("common.loading")}</p>}
        {isError && (
          <p className="nr-melding" role="alert">
            {t("nieuweReservering.dieren.laadFout")}
          </p>
        )}
        {!isLoading && !isError && alle.length === 0 && (
          <p className="nr-kaart__uitleg">{t("nieuweReservering.dieren.geen")}</p>
        )}

        <div className="nr-dieren">
          {zichtbaar.map((d) => {
            const aan = gekozen.includes(d.id);
            const foto = fotos[String(d.id)];
            const teken = geslachtTeken(d.geslacht);
            const jaren = leeftijd(d.geboortedatum, ctx);
            const soortRas = [d.diersoort, d.ras].filter(Boolean).join(" – ");
            return (
              <label key={d.id} className="nr-dier" data-gekozen={aan || undefined}>
                <input
                  type="checkbox"
                  className="nr-dier__vink"
                  checked={aan}
                  aria-label={d.naam ?? String(d.id)}
                  onChange={() => wissel(d.id)}
                />
                {foto ? (
                  <img className="nr-dier__foto" src={foto} alt="" />
                ) : (
                  <span className="nr-dier__foto nr-dier__foto--leeg" aria-hidden="true">
                    <PawPrint size={28} />
                  </span>
                )}
                <span className="nr-dier__tekst">
                  <strong className="nr-dier__naam">{d.naam ?? "—"}</strong>
                  {soortRas && <span className="nr-dier__soort">{soortRas}</span>}
                  <span className="nr-regel">
                    {teken && (
                      <span className="nr-dier__teken" aria-hidden="true">
                        {teken}
                      </span>
                    )}
                    {d.geslacht ?? "—"}
                    {jaren && (
                      <>
                        <span className="nr-punt">•</span>
                        {jaren}
                      </>
                    )}
                  </span>
                  <span className="nr-regel">
                    <CalendarDays size={15} aria-hidden="true" />
                    {verblijf(d, reserveringen, ctx)}
                  </span>
                  <span className="nr-dier__chips">
                    {entingStaat(d, ctx.vandaag) === "verlopen" && (
                      <span className="nr-chip" data-toon="rood">
                        {t("nieuweReservering.dieren.entingVerlopen")}
                      </span>
                    )}
                    {(d.stand?.medicatieActief ?? 0) > 0 && (
                      <span className="nr-chip" data-toon="accent">
                        {t("nieuweReservering.dieren.medicatie")}
                      </span>
                    )}
                    {aan && (
                      <span className="nr-chip nr-chip--rechts" data-toon="groen">
                        {t("nieuweReservering.dieren.geselecteerd")}
                      </span>
                    )}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <EigenElementen
          elementen={eigenElementen(stap)}
          dieren={[]}
          antwoorden={antwoorden}
          setAntwoorden={setAntwoorden}
          t={t}
          geprobeerd={verplicht.geprobeerd}
        />
        <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} />}
        rechts={<Volgende t={t} disabled={gekozen.length === 0} onClick={verplicht.verder(onVolgende)} />}
      />
    </>
  );
}

// ── Stap 4: nieuw dier toevoegen ─────────────────────────────────────────────────────────────────────

interface OptieRij {
  id: number;
  naam?: string;
  label?: string;
}

interface DierFormulier {
  naam: string;
  chip: string;
  geslacht: "" | "Mannelijk" | "Vrouwelijk";
  gecastreerd: boolean;
  geboortedatum: string;
  diersoortId: string;
  rasId: string;
  kruising: boolean;
  vachtId: string;
  kleurId: string;
  bijzonderheden: string;
}

const LEEG_DIER: DierFormulier = {
  naam: "",
  chip: "",
  geslacht: "",
  gecastreerd: false,
  geboortedatum: "",
  diersoortId: "",
  rasId: "",
  kruising: false,
  vachtId: "",
  kleurId: "",
  bijzonderheden: "",
};

/**
 * De voorstellen van de AI in het formulier, alleen in lege velden: wat de balie al invulde, blijft staan. De
 * keuzelijsten gaan via het {@code referenceId} dat de api al op een bestaande rij legde. De geboortedatum komt
 * alleen uit een paspoort.
 */
export function vulDierIn(
  f: DierFormulier,
  voorstellen: PhotoAiSuggestion[],
  metGeboortedatum: boolean,
): { formulier: DierFormulier; aantal: number } {
  const uit = { ...f };
  let aantal = 0;
  for (const s of voorstellen) {
    if (!s.value) continue;
    switch (s.field) {
      case "NAAM":
        if (!uit.naam.trim()) { uit.naam = s.value; aantal++; }
        break;
      case "CHIPNUMMER":
        if (!uit.chip.trim() && /^\d{15}$/.test(s.value.replace(/\s/g, ""))) { uit.chip = s.value.replace(/\s/g, ""); aantal++; }
        break;
      case "GESLACHT":
        if (!uit.geslacht && (s.value === "Mannelijk" || s.value === "Vrouwelijk")) { uit.geslacht = s.value; aantal++; }
        break;
      case "GEBOORTEDATUM":
        if (metGeboortedatum && !uit.geboortedatum && isDatum(s.value)) { uit.geboortedatum = s.value; aantal++; }
        break;
      case "DIERSOORT":
        if (!uit.diersoortId && s.referenceId != null) { uit.diersoortId = String(s.referenceId); aantal++; }
        break;
      case "RAS":
        if (!uit.rasId && s.referenceId != null) { uit.rasId = String(s.referenceId); aantal++; }
        break;
      case "VACHT":
        if (!uit.vachtId && s.referenceId != null) { uit.vachtId = String(s.referenceId); aantal++; }
        break;
      case "VACHT_KLEUR":
        if (!uit.kleurId && s.referenceId != null) { uit.kleurId = String(s.referenceId); aantal++; }
        break;
      default:
        break;
    }
  }
  return { formulier: uit, aantal };
}

function opties(data: unknown): { id: number; label: string }[] {
  return (Array.isArray(data) ? (data as OptieRij[]) : []).map((r) => ({ id: r.id, label: r.naam ?? r.label ?? String(r.id) }));
}

function NieuwDier({
  klant,
  ctx,
  onAndereKlant,
  onTerug,
  onToegevoegd,
  uit,
}: {
  uit?: boolean;
  klant: GekozenKlant;
  ctx: Context;
  onAndereKlant: () => void;
  onTerug: () => void;
  onToegevoegd: (dierId: number, melding: string | null) => void;
}) {
  const t = ctx.t as Vertaal;
  const [f, setF] = useState<DierFormulier>(LEEG_DIER);
  const [geprobeerd, setGeprobeerd] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [aiMelding, setAiMelding] = useState<string | null>(null);
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [sleept, setSleept] = useState(false);
  const [leest, setLeest] = useState(false);
  const fotoInvoer = useRef<HTMLInputElement | null>(null);
  const paspoortInvoer = useRef<HTMLInputElement | null>(null);
  const { mutateAsync, isLoading } = useCustomMutation<{ id: number; naam: string }>();

  const soorten = opties(useCustom({ url: "animals/reference/species", method: "get" }).data?.data);
  const rassen = opties(
    useCustom({ url: `animals/reference/breeds${f.diersoortId ? `?species=${f.diersoortId}` : ""}`, method: "get" }).data?.data,
  );
  const vachten = opties(useCustom({ url: "animals/reference/coats", method: "get" }).data?.data);
  const kleuren = opties(useCustom({ url: "animals/reference/colours", method: "get" }).data?.data);
  const aiData = useCustom<{ available: boolean }>({ url: "pension/dieren/ai-beschikbaar", method: "get" }).data?.data;
  const ai = aiData?.available === true;

  useEffect(() => {
    if (!foto || typeof URL.createObjectURL !== "function") {
      setFotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(foto);
    setFotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);

  const zet = <K extends keyof DierFormulier>(k: K, v: DierFormulier[K]) => setF((x) => ({ ...x, [k]: v }));
  const chipFout = f.chip.trim() !== "" && !/^\d{15}$/.test(f.chip.replace(/\s/g, ""));
  const ontbreekt = !f.naam.trim() || !f.diersoortId;

  // Een paspoort heeft de gegevens vaak over twee bladzijden (chip op de ene, geboortedatum op de andere): elke
  // bladzijde wordt gelezen, en wat een eerdere al invulde blijft staan (vulDierIn vult alleen lege velden).
  const leesVoor = async (bestanden: File | File[], paspoort: boolean) => {
    const lijst = Array.isArray(bestanden) ? bestanden.slice(0, 10) : [bestanden];
    setLeest(true);
    setAiMelding(null);
    const voorstellen: Parameters<typeof vulDierIn>[1] = [];
    let melding: string | null = null;
    let fout = false;
    for (const bestand of lijst) {
      try {
        const res = paspoort ? await analyseerPaspoort(bestand) : await analyseerDierFoto(bestand);
        voorstellen.push(...(res.suggestions ?? []));
        melding ??= res.message || null;
      } catch {
        fout = true;
      }
    }
    // Eén keer invullen, met alles wat de bladzijden opleverden; het aantal rekenen we op wat er nu staat.
    const aantal = vulDierIn(f, voorstellen, paspoort).aantal;
    setF((x) => vulDierIn(x, voorstellen, paspoort).formulier);
    setAiMelding(
      aantal > 0 ? t("nieuweReservering.dier.aiIngevuld", { args: [aantal] })
        : fout ? t("nieuweReservering.dier.aiFout") : melding || t("nieuweReservering.dier.aiNiets"),
    );
    setLeest(false);
  };

  const kiesFoto = (bestand: File | undefined) => {
    if (!bestand) return;
    if (!FOTO_TYPES.includes(bestand.type) || bestand.size > FOTO_MAX) {
      setFout(t("nieuweReservering.dier.fotoOngeldig"));
      return;
    }
    setFout(null);
    setFoto(bestand);
    if (ai) void leesVoor(bestand, false);
  };

  const opslaan = async () => {
    setGeprobeerd(true);
    setFout(null);
    if (ontbreekt || chipFout) return;
    const leeg = (v: string) => (v.trim() === "" ? null : v.trim());
    try {
      const res = await mutateAsync({
        url: `pension/klanten/${klant.id}/dieren`,
        method: "post",
        values: {
          naam: f.naam.trim(),
          chipnummer: leeg(f.chip.replace(/\s/g, "")),
          geslacht: f.geslacht === "" ? null : f.geslacht,
          gecastreerd: f.gecastreerd,
          geboortedatum: f.geboortedatum === "" ? null : f.geboortedatum,
          diersoortId: Number(f.diersoortId),
          rasId: f.rasId ? Number(f.rasId) : null,
          kruising: f.kruising,
          vachtId: f.vachtId ? Number(f.vachtId) : null,
          kleurId: f.kleurId ? Number(f.kleurId) : null,
          bijzonderheden: leeg(f.bijzonderheden),
        },
        successNotification: false,
        errorNotification: false,
      });
      let melding: string | null = null;
      if (foto) {
        try {
          await bewaarDierFoto(res.data.id, foto);
        } catch {
          melding = t("nieuweReservering.dier.fotoNietOpgeslagen");
        }
      }
      onToegevoegd(res.data.id, melding);
    } catch (e) {
      setFout(foutTekst(t, e, "nieuweReservering.dier"));
    }
  };

  const keuzelijst = (waarde: string, onChange: (v: string) => void, lijst: { id: number; label: string }[], leegLabel: string, fout?: boolean) => (
    <select className="nr-invoer" data-fout={fout || undefined} value={waarde} onChange={(e) => onChange(e.target.value)}>
      <option value="">{leegLabel}</option>
      {lijst.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <form
      className="nr__vorm"
      noValidate
      aria-label={t("nieuweReservering.dier.titel")}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isLoading && !uit) void opslaan();
      }}
    >
      <div className="nr__inhoud">
        <KlantBalk
          klant={klant}
          label={t("nieuweReservering.dier.voor")}
          knop={t("nieuweReservering.dier.andereKlant")}
          knopIcoon={<UserRound size={16} aria-hidden="true" />}
          onKnop={onAndereKlant}
        />
        <h3 className="nr-kaart__titel">{t("nieuweReservering.dier.titel")}</h3>
        <p className="nr-kaart__uitleg">{t("nieuweReservering.dier.uitleg")}</p>

        <div className="nr-dierform">
          <section className="nr-paneel" aria-label={t("nieuweReservering.dier.gegevens")}>
            <div className="nr-paneel__kop">
              <span className="nr-rond" aria-hidden="true">
                <PawPrint size={20} />
              </span>
              <h4>{t("nieuweReservering.dier.gegevens")}</h4>
            </div>
            <div className="nr-rij nr-rij--twee">
              <Veld label={t("nieuweReservering.dier.naam")} verplicht>
                <input
                  className="nr-invoer"
                  data-fout={(geprobeerd && !f.naam.trim()) || undefined}
                  maxLength={50}
                  placeholder={t("nieuweReservering.dier.naamPlaats")}
                  value={f.naam}
                  onChange={(e) => zet("naam", e.target.value)}
                />
              </Veld>
              <Veld label={t("nieuweReservering.dier.chip")}>
                <input
                  className="nr-invoer"
                  inputMode="numeric"
                  data-fout={chipFout || undefined}
                  maxLength={19}
                  placeholder={t("nieuweReservering.dier.chipPlaats")}
                  value={f.chip}
                  onChange={(e) => zet("chip", e.target.value)}
                />
              </Veld>
              <div className="nr-met-schakel">
                <Veld label={t("nieuweReservering.dier.geslacht")}>
                  <select
                    className="nr-invoer"
                    value={f.geslacht}
                    onChange={(e) => zet("geslacht", e.target.value as DierFormulier["geslacht"])}
                  >
                    <option value="">{t("nieuweReservering.dier.onbekend")}</option>
                    <option value="Mannelijk">{t("nieuweReservering.dier.mannelijk")}</option>
                    <option value="Vrouwelijk">{t("nieuweReservering.dier.vrouwelijk")}</option>
                  </select>
                </Veld>
                <Schakel label={t("nieuweReservering.dier.gecastreerd")} aan={f.gecastreerd} onChange={(v) => zet("gecastreerd", v)} />
              </div>
              <Veld label={t("nieuweReservering.dier.geboortedatum")}>
                <DateField className="nr-invoer" value={f.geboortedatum} onChange={(e) => zet("geboortedatum", e.target.value)} />
              </Veld>
              <Veld label={t("nieuweReservering.dier.diersoort")} verplicht>
                {keuzelijst(
                  f.diersoortId,
                  (v) => setF((x) => ({ ...x, diersoortId: v, rasId: "" })),
                  soorten,
                  t("nieuweReservering.dier.kiesSoort"),
                  geprobeerd && !f.diersoortId,
                )}
              </Veld>
              <div className="nr-met-schakel">
                <Veld label={t("nieuweReservering.dier.ras")}>
                  {keuzelijst(f.rasId, (v) => zet("rasId", v), rassen, t("nieuweReservering.dier.kiesRas"))}
                </Veld>
                <Schakel label={t("nieuweReservering.dier.kruising")} aan={f.kruising} onChange={(v) => zet("kruising", v)} />
              </div>
              <Veld label={t("nieuweReservering.dier.vacht")}>
                {keuzelijst(f.vachtId, (v) => zet("vachtId", v), vachten, t("nieuweReservering.dier.kiesVacht"))}
              </Veld>
              <Veld label={t("nieuweReservering.dier.kleur")}>
                {keuzelijst(f.kleurId, (v) => zet("kleurId", v), kleuren, t("nieuweReservering.dier.kiesKleur"))}
              </Veld>
            </div>
            <Veld label={t("nieuweReservering.dier.bijzonderheden")}>
              <textarea
                className="nr-invoer"
                maxLength={BIJZONDERHEDEN_MAX}
                placeholder={t("nieuweReservering.dier.bijzonderhedenPlaats")}
                value={f.bijzonderheden}
                onChange={(e) => zet("bijzonderheden", e.target.value)}
              />
            </Veld>
            <span className="nr-teller" aria-hidden="true">
              {f.bijzonderheden.length}/{BIJZONDERHEDEN_MAX}
            </span>
            {chipFout && <p className="nr-melding">{t("nieuweReservering.dier.chipOngeldig")}</p>}
            {geprobeerd && ontbreekt && (
              <p className="nr-melding" role="alert">
                {t("nieuweReservering.dier.verplicht")}
              </p>
            )}
          </section>

          <section className="nr-paneel" aria-label={t("nieuweReservering.dier.foto")}>
            <div className="nr-paneel__kop">
              <span className="nr-rond" aria-hidden="true">
                <ImagePlus size={20} />
              </span>
              <div>
                <h4>{t("nieuweReservering.dier.foto")}</h4>
                <p>{ai ? t("nieuweReservering.dier.fotoUitlegAi") : t("nieuweReservering.dier.fotoUitleg")}</p>
              </div>
            </div>
            <input
              ref={fotoInvoer}
              className="nr-verborgen"
              type="file"
              accept={FOTO_TYPES.join(",")}
              capture="environment"
              aria-label={t("nieuweReservering.dier.fotoKiezen")}
              tabIndex={-1}
              onChange={(e) => {
                kiesFoto(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="nr-upload"
              data-sleept={sleept || undefined}
              onClick={() => fotoInvoer.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setSleept(true);
              }}
              onDragLeave={() => setSleept(false)}
              onDrop={(e) => {
                e.preventDefault();
                setSleept(false);
                kiesFoto(e.dataTransfer.files?.[0]);
              }}
            >
              {fotoUrl ? (
                <img src={fotoUrl} alt={t("nieuweReservering.dier.fotoVoorbeeld")} />
              ) : (
                <ImagePlus size={32} aria-hidden="true" />
              )}
              <strong>{foto ? t("nieuweReservering.dier.fotoAnders") : t("nieuweReservering.dier.fotoKlik")}</strong>
              <span>{t("nieuweReservering.dier.fotoSleep")}</span>
            </button>
            <p className="nr-klein">{t("nieuweReservering.dier.fotoFormaten")}</p>
            {ai && (
              <>
                <div className="nr-scheiding">
                  <span>{t("nieuweReservering.dier.of")}</span>
                </div>
                <input
                  ref={paspoortInvoer}
                  className="nr-verborgen"
                  type="file"
                  accept={FOTO_TYPES.join(",")}
                  aria-label={t("nieuweReservering.dier.paspoort")}
                  tabIndex={-1}
                  multiple
                  onChange={(e) => {
                    const bestanden = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    if (bestanden.length) void leesVoor(bestanden, true);
                  }}
                />
                <button type="button" className="nr-scan" disabled={leest} onClick={() => paspoortInvoer.current?.click()}>
                  <strong>
                    <ScanLine size={18} aria-hidden="true" />
                    {t("nieuweReservering.dier.paspoort")}
                  </strong>
                  <span>{t("nieuweReservering.dier.paspoortUitleg")}</span>
                </button>
                <div className="nr-ai">
                  <Sparkles size={20} aria-hidden="true" />
                  <div>
                    <strong>{t("nieuweReservering.dier.aiTitel")}</strong>
                    {leest ? t("nieuweReservering.dier.aiBezig") : (aiMelding ?? t("nieuweReservering.dier.aiUitleg"))}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
        {fout && (
          <p className="nr-melding" role="alert">
            {fout}
          </p>
        )}
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} label={t("nieuweReservering.dier.terug")} />}
        rechts={
          <button type="submit" className="nr-knop nr-knop--hoofd" disabled={isLoading || uit}>
            {t("nieuweReservering.dier.toevoegen")}
            <ChevronRight size={17} aria-hidden="true" />
          </button>
        }
      />
    </form>
  );
}

// ── Stap 5: periode ──────────────────────────────────────────────────────────────────────────────────

function PeriodeKiezen({
  klant,
  dierIds,
  periode: p,
  setPeriode,
  ctx,
  onKlant,
  onDieren,
  onTerug,
  onVolgende,
  stap,
  antwoorden = GEEN_ANTWOORDEN,
  setAntwoorden = NIETS,
}: {
  onTerug?: () => void;
  stap?: WizardStap;
  antwoorden?: Antwoorden;
  setAntwoorden?: Dispatch<SetStateAction<Antwoorden>>;
  klant: GekozenKlant;
  dierIds: number[];
  periode: Periode;
  setPeriode: Dispatch<SetStateAction<Periode>>;
  ctx: Context;
  onKlant: () => void;
  onDieren: () => void;
  onVolgende: () => void;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const fotos = useAnimalPhotoUrls(gekozen.map((d) => d.id));
  const tijden = useBrengHaaltijden();
  const brengTijden = tijdenVoor(tijden, p.aankomst, "brengen");
  const haalTijden = tijdenVoor(tijden, p.vertrek, "halen");
  const brengOpen = pensionOpen(tijden, p.aankomst, "brengen");
  const haalOpen = pensionOpen(tijden, p.vertrek, "halen");
  const geldig = geldigePeriode(p) && brengOpen && haalOpen;
  const nachten = geldig ? dagenTussen(p.aankomst, p.vertrek) : null;
  const zet = <K extends keyof Periode>(k: K, v: Periode[K]) => setPeriode((x) => ({ ...x, [k]: v }));
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  useEffect(() => {
    if (brengTijden.length > 0 && !brengTijden.includes(p.aankomstTijd)) setPeriode((x) => ({ ...x, aankomstTijd: brengTijden[0] }));
    if (haalTijden.length > 0 && !haalTijden.includes(p.vertrekTijd)) setPeriode((x) => ({ ...x, vertrekTijd: haalTijden[haalTijden.length - 1] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brengTijden.join(","), haalTijden.join(",")]);
  const rond = rondBlok(stap, "BESCHIKBAARHEID");
  const beschikbaarBlok = blokElement(stap, "BESCHIKBAARHEID");
  const locatieBlok = blokElement(stap, "LOCATIE");
  const locatieTitel = locatieBlok ? blokTitel(t, "PERIODE", locatieBlok) : t("nieuweReservering.periode.locatie");
  const kop = stapKop(t, stap, "periode");
  const eigen = (lijst: WizardElement[]) => (
    <EigenElementen
      elementen={lijst}
      dieren={gekozen}
      antwoorden={antwoorden}
      setAntwoorden={setAntwoorden}
      t={t}
      geprobeerd={verplicht.geprobeerd}
    />
  );

  const locatie = useLocatie();
  const params = new URLSearchParams({ van: p.aankomst, tot: p.vertrek });
  dierIds.forEach((id) => params.append("dierIds", String(id)));
  const controle = useCustom<Beschikbaarheid>({
    url: metLocatie(`pension/beschikbaarheid?${params}`, locatie.gekozen),
    method: "get",
    queryOptions: { enabled: geldig && dierIds.length > 0 && blokAan(stap, "BESCHIKBAARHEID") },
  });
  const uitslag = geldig ? controle.data?.data : undefined;
  const staat = !geldig
    ? "leeg"
    : controle.isFetching && !uitslag
      ? "bezig"
      : controle.isError
        ? "fout"
        : uitslag?.beschikbaar
          ? "ok"
          : uitslag
            ? "vol"
            : "bezig";
  const nachtTekst = (n: number) => (n === 1 ? t("nieuweReservering.periode.nacht") : t("nieuweReservering.periode.nachten", { args: [n] }));
  const kort = (iso: string) =>
    new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`)).replace(".", "");

  const tijdKeuze = (label: string, waarde: string, onChange: (v: string) => void, lijst: string[]) => (
    <Veld label={label}>
      <span className="nr-met-icoon">
        <Clock size={17} aria-hidden="true" />
        <select className="nr-invoer" value={waarde} disabled={lijst.length === 0} onChange={(e) => onChange(e.target.value)}>
          {(lijst.includes(waarde) ? lijst : [waarde, ...lijst]).map((tijd) => (
            <option key={tijd} value={tijd}>
              {tijd}
            </option>
          ))}
        </select>
      </span>
    </Veld>
  );

  /**
   * Welke dagen de kalender grijs houdt. Zolang een vestiging geen tijden heeft ingericht staat elke dag
   * open; daarna is een dag zonder blok een dag waarop het pension geen dieren aanneemt of meegeeft, en
   * die hoort niet aan te klikken te zijn. De melding eronder blijft staan voor wie de datum intypt.
   */
  const dichtVoor = (soort: "brengen" | "halen") => (iso: string) => !pensionOpen(tijden, iso, soort);

  /** Wat het pension die dag doet: de blokken, of dat het die dag niet brengt of haalt. */
  const tijdUitleg = (iso: string, soort: "brengen" | "halen") => {
    if (!tijden.ingesteld || !isDatum(iso)) return null;
    const vensters = venstersVoor(tijden, iso, soort);
    return (
      <p className={vensters.length === 0 ? "nr-melding" : "nr-klein"} role={vensters.length === 0 ? "status" : undefined}>
        {vensters.length === 0
          ? t(`nieuweReservering.periode.${soort}Dicht`)
          : t(`nieuweReservering.periode.${soort}Op`, { args: [vensterTekst(vensters, t("nieuweReservering.periode.en"))] })}
      </p>
    );
  };

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk
          klant={klant}
          knop={t("nieuweReservering.periode.klantBekijken")}
          knopIcoon={<UserRound size={16} aria-hidden="true" />}
          onKnop={onKlant}
        />
        <section className="nr-gekozen nr-gekozen--dieren" aria-label={t("nieuweReservering.periode.dieren")}>
          <div className="nr-gekozen__rij">
            <span className="nr-rond" aria-hidden="true">
              <PawPrint size={20} />
            </span>
            <strong>{t("nieuweReservering.periode.dieren")}</strong>
            <span className="nr-gekozen__aantal">
              {dierIds.length === 1 ? t("nieuweReservering.kiezen.dierEen") : t("nieuweReservering.kiezen.dieren", { args: [dierIds.length] })}
            </span>
          </div>
          <div className="nr-gekozen__rij nr-gekozen__rij--start">
            <div className="nr-mini-dieren">
              {gekozen.map((d) => {
                const foto = fotos[String(d.id)];
                const jaren = leeftijd(d.geboortedatum, ctx);
                return (
                  <div key={d.id} className="nr-mini">
                    {foto ? (
                      <img className="nr-mini__foto" src={foto} alt="" />
                    ) : (
                      <span className="nr-mini__foto nr-dier__foto--leeg" aria-hidden="true">
                        <PawPrint size={22} />
                      </span>
                    )}
                    <span className="nr-dier__tekst">
                      <strong>{d.naam ?? "—"}</strong>
                      <span className="nr-dier__soort">{[d.diersoort, d.ras].filter(Boolean).join(" · ")}</span>
                      <span className="nr-regel">
                        {geslachtTeken(d.geslacht)} {[d.geslacht, jaren].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
            <button type="button" className="nr-knop nr-knop--rand" onClick={onDieren}>
              <Pencil size={16} aria-hidden="true" />
              {t("nieuweReservering.periode.dierenAanpassen")}
            </button>
          </div>
        </section>

        <h3 className="nr-kaart__titel">{kop.titel}</h3>
        <p className="nr-kaart__uitleg">{kop.uitleg}</p>

        <div className="nr-periode">
          <section className="nr-moment" aria-label={t("nieuweReservering.periode.aankomst")}>
            <div className="nr-paneel__kop">
              <span className="nr-rond nr-rond--groot" aria-hidden="true">
                <LogIn size={22} />
              </span>
              <div>
                <h4>{t("nieuweReservering.periode.aankomst")}</h4>
                <p>{t("nieuweReservering.periode.aankomstVraag")}</p>
              </div>
            </div>
            <div className="nr-rij nr-rij--twee">
              <Veld label={t("nieuweReservering.periode.aankomstdatum")}>
                <DateField
                  className="nr-invoer"
                  value={p.aankomst}
                  dagDicht={dichtVoor("brengen")}
                  onChange={(e) => zet("aankomst", e.target.value)}
                />
              </Veld>
              {tijdKeuze(t("nieuweReservering.periode.aankomsttijd"), p.aankomstTijd, (v) => zet("aankomstTijd", v), brengTijden)}
            </div>
            {tijdUitleg(p.aankomst, "brengen")}
          </section>
          <ArrowRight className="nr-periode__pijl" size={24} aria-hidden="true" />
          <section className="nr-moment nr-moment--vertrek" aria-label={t("nieuweReservering.periode.vertrek")}>
            <div className="nr-paneel__kop">
              <span className="nr-rond nr-rond--groot" aria-hidden="true">
                <Home size={22} />
              </span>
              <div>
                <h4>{t("nieuweReservering.periode.vertrek")}</h4>
                <p>{t("nieuweReservering.periode.vertrekVraag")}</p>
              </div>
            </div>
            <div className="nr-rij nr-rij--twee">
              <Veld label={t("nieuweReservering.periode.vertrekdatum")}>
                <DateField
                  className="nr-invoer"
                  min={p.aankomst || undefined}
                  value={p.vertrek}
                  dagDicht={dichtVoor("halen")}
                  onChange={(e) => zet("vertrek", e.target.value)}
                />
              </Veld>
              {tijdKeuze(t("nieuweReservering.periode.vertrektijd"), p.vertrekTijd, (v) => zet("vertrekTijd", v), haalTijden)}
            </div>
            {tijdUitleg(p.vertrek, "halen")}
          </section>
        </div>

        <section className="nr-duur" aria-label={t("nieuweReservering.periode.duur")}>
          <span className="nr-rond nr-rond--groot" aria-hidden="true">
            <CalendarDays size={22} />
          </span>
          <div>
            <span className="nr-klein">{t("nieuweReservering.periode.duur")}</span>
            {nachten != null ? (
              <>
                <strong className="nr-duur__getal">{nachtTekst(nachten)}</strong>
                <span className="nr-regel">
                  {langeDatum(p.aankomst, locale)} {p.aankomstTijd}
                  <ArrowRight size={15} aria-hidden="true" />
                  {langeDatum(p.vertrek, locale)} {p.vertrekTijd}
                </span>
              </>
            ) : (
              <strong className="nr-duur__getal nr-duur__getal--leeg">
                {!isDatum(p.aankomst) || !isDatum(p.vertrek)
                  ? t("nieuweReservering.periode.kies")
                  : p.vertrek < p.aankomst
                    ? t("nieuweReservering.periode.ongeldig")
                    : // De datums kloppen; wat ontbreekt is een tijd, bijvoorbeeld omdat het pension die dag dicht is.
                      t("nieuweReservering.periode.kiesTijd")}
              </strong>
            )}
          </div>
        </section>

        {eigen(rond.voor)}
        {/* De locatie hoort bij de periode: de beschikbaarheid hieronder geldt voor deze locatie, en wie wisselt
            ziet meteen of daar in dezelfde periode wel plek is. Bij één locatie valt er niets te kiezen. */}
        {blokAan(stap, "LOCATIE") && locatie.keuzes.length > 1 && (
          <section className="nr-locatie" aria-label={locatieTitel}>
            <span className="nr-rond" aria-hidden="true">
              <MapPin size={20} />
            </span>
            <div className="nr-locatie__tekst">
              <strong>{locatieTitel}</strong>
              <span className="nr-klein">{t("nieuweReservering.periode.locatieUitleg")}</span>
            </div>
            <Veld label={t("nieuweReservering.periode.locatieKiezen")}>
              <select
                className="nr-invoer"
                value={locatie.gekozen ?? ""}
                onChange={(e) => locatie.kies(Number(e.target.value))}
              >
                {locatie.keuzes.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.naam}
                  </option>
                ))}
              </select>
            </Veld>
          </section>
        )}
        {blokAan(stap, "BESCHIKBAARHEID") && (
        <section className="nr-beschikbaar" data-staat={staat} aria-label={t("nieuweReservering.periode.beschikbaarheid")}>
          <span className="nr-rond nr-rond--groot" aria-hidden="true">
            {staat === "vol" ? <AlertTriangle size={22} /> : <Check size={22} />}
          </span>
          <div className="nr-beschikbaar__tekst">
            <strong>{beschikbaarBlok ? blokTitel(t, "PERIODE", beschikbaarBlok) : t("nieuweReservering.periode.beschikbaarheid")}</strong>
            <span className="nr-regel">
              {nachten != null
                ? [
                    dierIds.length === 1 ? t("nieuweReservering.kiezen.dierEen") : t("nieuweReservering.kiezen.dieren", { args: [dierIds.length] }),
                    t("nieuweReservering.periode.tm", { args: [kort(p.aankomst), kort(p.vertrek)] }),
                    nachtTekst(nachten),
                  ].join(" · ")
                : t("nieuweReservering.periode.kiesEerst")}
            </span>
          </div>
          <div className="nr-beschikbaar__uitslag">
            {staat === "ok" && (
              <div className="nr-uitslag" data-toon="groen" role="status">
                <Check size={20} aria-hidden="true" />
                <div>
                  <strong>{t("nieuweReservering.periode.ok")}</strong>
                  <span>
                    {uitslag?.capaciteitIngesteld ? t("nieuweReservering.periode.okUitleg") : t("nieuweReservering.periode.geenCapaciteit")}
                  </span>
                </div>
              </div>
            )}
            {staat === "vol" && uitslag && (
              <div className="nr-uitslag" data-toon="rood" role="status">
                <AlertTriangle size={20} aria-hidden="true" />
                <div>
                  <strong>{t("nieuweReservering.periode.vol")}</strong>
                  <ul>
                    {uitslag.knelpunten.slice(0, 5).map((k) => (
                      <li key={`${k.datum}-${k.diersoort}-${k.reden}`}>
                        {k.reden === "VOL"
                          ? t("nieuweReservering.periode.knelpuntVol", { args: [kort(k.datum), k.diersoort ?? ""] })
                          : t("nieuweReservering.periode.knelpuntCapaciteit", { args: [kort(k.datum), k.diersoort ?? "", k.vrij ?? 0] })}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {staat === "bezig" && <p className="nr-klein">{t("nieuweReservering.periode.bezig")}</p>}
            {staat === "fout" && (
              <p className="nr-melding" role="alert">
                {t("nieuweReservering.periode.fout")}
              </p>
            )}
            <button
              type="button"
              className="nr-knop nr-knop--rand"
              disabled={!geldig || controle.isFetching}
              onClick={() => void controle.refetch()}
            >
              <RefreshCw size={16} aria-hidden="true" />
              {t("nieuweReservering.periode.opnieuw")}
            </button>
          </div>
        </section>
        )}
        {eigen(rond.na)}
        <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug ?? onDieren} label={onTerug ? undefined : t("nieuweReservering.dier.terug")} />}
        rechts={<Volgende t={t} disabled={!geldig || staat === "vol"} onClick={verplicht.verder(onVolgende)} />}
      />
    </>
  );
}
